import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export interface UserPreferences {
  position: string | null;
  idpExperience: string | null;
}

export interface QuickStartStatus {
  userEntityRef: string;
  completed: boolean;
  completedAt: string | null;
  position: string | null;
  idpExperience: string | null;
}

export interface QuickStartApi {
  getStatus(): Promise<QuickStartStatus>;
  savePreferences(preferences: UserPreferences): Promise<QuickStartStatus>;
  complete(): Promise<QuickStartStatus>;
  reset(): Promise<{ reset: boolean }>;
}

export const quickStartApiRef = createApiRef<QuickStartApi>({
  id: 'plugin.quick-start.api',
});

export class QuickStartApiClient implements QuickStartApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('quick-start');
  }

  async getStatus(): Promise<QuickStartStatus> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/status`);

    if (!response.ok) {
      throw new Error(
        `Failed to get quick start status: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async savePreferences(
    preferences: UserPreferences,
  ): Promise<QuickStartStatus> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/preferences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(preferences),
    });

    if (!response.ok) {
      throw new Error(`Failed to save preferences: ${response.statusText}`);
    }

    return response.json();
  }

  async complete(): Promise<QuickStartStatus> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to complete quick start: ${response.statusText}`);
    }

    return response.json();
  }

  async reset(): Promise<{ reset: boolean }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/reset`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to reset quick start: ${response.statusText}`);
    }

    return response.json();
  }
}
